import { Icon } from '../../../shared/components/Icon';
import type { PublicExperience } from '../types';
import { formatDateRange } from './formatDateRange';
import { Reveal, RevealGroup } from './Reveal';
import { TagList } from './TagList';

export function Experience({
  entries,
  resumeUrl,
}: {
  readonly entries: readonly PublicExperience[];
  readonly resumeUrl: string | null;
}) {
  if (entries.length === 0) {
    return (
      <Reveal>
        <p className="m-0 rounded-lg border border-dashed border-line px-6 py-11 text-center text-muted">
          Experience is being written up.
        </p>
      </Reveal>
    );
  }

  return (
    <>
      <RevealGroup>
        <ol className="m-0 grid list-none gap-2 p-0">
          {entries.map((entry) => (
            <Reveal as="li" key={entry.id} className="entry-row">
              <div
                className="text-[11px] font-semibold uppercase tracking-[0.1em] text-faint tabular-nums whitespace-nowrap sm:pt-[5px]"
                data-numeric
              >
                {formatDateRange(entry.startDate, entry.endDate)}
              </div>

              <div>
                <h3 className="entry-title">
                  {entry.externalUrl === null ? (
                    `${entry.role} · ${entry.company}`
                  ) : (
                    <a href={entry.externalUrl} target="_blank" rel="noreferrer">
                      {entry.role} · {entry.company}
                      <Icon name="arrow" size={14} className="entry-arrow" />
                    </a>
                  )}
                </h3>

                <p className="mt-0 mb-3 text-[14.5px]">{entry.summary}</p>

                <TagList tags={entry.tags} />
              </div>
            </Reveal>
          ))}
        </ol>
      </RevealGroup>

      {resumeUrl !== null && (
        <Reveal>
          <a
            href={resumeUrl}
            target="_blank"
            rel="noreferrer"
            className="group mt-[26px] inline-flex items-center gap-2 text-[14.5px] font-medium text-text transition-colors duration-250 hover:text-accent"
          >
            View full résumé
            <Icon
              name="arrow"
              size={15}
              className="transition-transform duration-300 group-hover:translate-x-[3px] group-hover:-translate-y-[3px]"
            />
          </a>
        </Reveal>
      )}
    </>
  );
}
